import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DeleteItem, SuccessfulDelete } from "../../components/modal";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import { getNotificationById } from "../../api/notifications";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import { getUserFacingError } from "../../utils/userFacingError";
import { getNotificationActionLabel, getNotificationActionUrl, getNotificationDestinationDescription } from "../../utils/notificationNavigation";

const formatDateLabel = (value) => {
  if (!value) {
    return "Unknown date";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown date";
  }

  return date.toLocaleDateString(undefined, {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const NotificationDetail = () => {
  const [searchParams] = useSearchParams();
  const notificationId = searchParams.get("id");
  const navigate = useNavigate();

  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSuccessfulDeleteModal, setOpenSuccessfulDeleteModal] =
    useState(false);

  useEffect(() => {
    const loadNotification = async () => {
      if (!notificationId) {
        setError("Notification ID is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await getNotificationById(notificationId);
        setNotification(response.notification || null);
      } catch (loadError) {
        setError(getUserFacingError(loadError, "Unable to load notification details."));
      } finally {
        setLoading(false);
      }
    };

    loadNotification();
  }, [notificationId]);

  const cancelDeleteModal = () => setOpenDeleteModal(false);
  const confirmDeleteModal = () => {
    setOpenDeleteModal(false);
    setOpenSuccessfulDeleteModal(true);
  };

  const openModal = () => setOpenDeleteModal(true);
  const closeSuccessfulDeleteModal = () => {
    setOpenSuccessfulDeleteModal(false);
  };
  const actionUrl = getNotificationActionUrl(notification);
  const destination = getNotificationDestinationDescription(notification);

  return (
    <div className="p-6 md:p-6 font-Outfit min-h-screen">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">Notification details</p>
          <h1 className="text-2xl font-semibold text-gray-900">
            {notification?.title || "Notification details"}
          </h1>
          {notification?.type ? (
            <p className="mt-1 text-sm text-gray-500">Type: {notification.type}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Back to notifications
          </button>
          <button
            type="button"
            onClick={openModal}
            className="rounded-lg border border-red-500 bg-white px-4 py-2 text-sm font-medium text-red-500 hover:bg-red-500 hover:text-white"
          >
            Delete notification
          </button>
        </div>
      </div>

      <section className="mb-5 rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {actionUrl ? (destination?.isDirect ? "Direct destination" : "Related workspace") : "No linked destination"}
        </p>
        {actionUrl ? (
          <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-700">
              {destination?.isDirect
                ? `Opens ${destination.location}`
                : `No item-specific link was saved. Open ${destination?.location} to find the related item.`}
            </p>
            <button type="button" onClick={() => navigate(actionUrl)} className="inline-flex shrink-0 items-center justify-center rounded-lg bg-[#1234CD] px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">{getNotificationActionLabel(notification)}</button>
          </div>
        ) : <p className="mt-1 text-sm text-slate-600">This notification does not include a route to another page.</p>}
      </section>

      {loading ? (
        <FetchLoadingState message="Loading notification..." className="min-h-[360px] rounded-xl bg-white shadow-sm" />
      ) : error ? (
        <div className="rounded-xl bg-white p-10 text-center text-sm text-red-600 shadow-sm">
          {error}
        </div>
      ) : notification ? (
        <div className="bg-white p-6 rounded-2xl shadow-sm">
          <div className="mb-6 flex flex-col gap-3 rounded-2xl bg-slate-50 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
              <span>{formatDateLabel(notification.sentAt || notification.createdAt)}</span>
              <span>{formatTime(notification.sentAt || notification.createdAt)}</span>
            </div>
            <p className="text-sm text-gray-500">
              {notification.isRead ? "Read" : "Unread"}
            </p>
          </div>

          <div className="rounded-2xl bg-gray-50 p-6 text-sm leading-7 text-gray-700">
            {notification.message || "No additional details are available."}
          </div>
        </div>
      ) : (
        <div className="rounded-xl bg-white p-10 text-center text-sm text-gray-500 shadow-sm">
          Notification not found.
        </div>
      )}

      <DeleteItem isOpen={openDeleteModal} onClose={cancelDeleteModal}>
        <div>
          <img
            className="w-40 mx-auto animate-pulse"
            src={DeleteImg}
            alt="verify del"
          />

          <h1 className="text-lg text-center text-[#000000] font-bold">
            Continue to delete this notification?
          </h1>
          <p className="text-sm text-center mt-2">Action cannot be undone</p>
        </div>
        <div className="flex items-center py-4 gap-4 my-4">
          <button
            onClick={cancelDeleteModal}
            className="border-[#1233CC] py-1 border-2 text-[#1233CC] font-medium px-4 rounded-[10px] w-[102px]"
          >
            No
          </button>
          <button
            className="text-[16px] font-medium px-4 py-1 bg-[#1233CC] rounded-[10px] w-[102px] text-white"
            onClick={confirmDeleteModal}
          >
            Yes
          </button>
        </div>
      </DeleteItem>

      <SuccessfulDelete
        isOpen={openSuccessfulDeleteModal}
        onClose={closeSuccessfulDeleteModal}
      >
        <div>
          <img
            src={verifyImg}
            alt=" verify delete"
            className="w-20 animate-ping mx-auto"
          />
          <div className="text-center mt-6">
            <h2 className="text-black text-[20px] leading-[27px] font-bold mb-2">
              Notification deleted
            </h2>
            <p className="text-[#74777B] text-[14px] leading-[18.9px]">
              The notification was deleted successfully.
            </p>
          </div>
        </div>
      </SuccessfulDelete>
    </div>
  );
};

export default NotificationDetail;
